import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users as UsersIcon, Plus, X, Share2, Mail, Trash2, ShieldCheck, ShieldAlert, MapPin, UserPlus } from "lucide-react";
import { toast } from "sonner";

const REASON_LABELS = {
  cert_missing: "No certification on file",
  cert_lapsed: "Certification lapsed",
  insurance_missing: "No insurance on file",
  insurance_lapsed: "Insurance lapsed",
  no_shared_region: "No shared region",
};

export default function Teams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const ctl = useListControls(teams, { name: (t) => t.name });
  const [showCreate, setShowCreate] = useState(false);
  const [activeInvite, setActiveInvite] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [newTeam, setNewTeam] = useState({ name: "", tagline: "", description: "" });
  const [eligible, setEligible] = useState([]);  // eligible coaches for the currently-open invite panel
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [adding, setAdding] = useState(null);  // coach id currently being added

  const load = async () => {
    const { data } = await api.get("/teams");
    setTeams(data);
  };
  useEffect(() => { load(); }, []);

  const openInvite = async (teamId) => {
    setActiveInvite(teamId);
    setEligible([]);
    setEligibleLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/eligible-coaches`);
      setEligible(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load coaches");
    } finally { setEligibleLoading(false); }
  };

  const closeInvite = () => {
    setActiveInvite(null);
    setInviteEmail("");
    setEligible([]);
  };

  const createTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/teams", newTeam);
      toast.success("Team created");
      setShowCreate(false);
      setNewTeam({ name: "", tagline: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addExistingCoach = async (teamId, coach) => {
    if (!coach.eligible) return;
    setAdding(coach.id);
    try {
      await api.post(`/teams/${teamId}/invite`, { email: coach.email });
      toast.success(`${coach.name} added to team`);
      // Refresh both list-of-teams and the eligible-coaches modal
      await load();
      const { data } = await api.get(`/teams/${teamId}/eligible-coaches`);
      setEligible(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(null); }
  };

  const invite = async (teamId) => {
    if (!inviteEmail) return;
    try {
      const { data } = await api.post(`/teams/${teamId}/invite`, { email: inviteEmail });
      if (data?.status === "invite_sent") {
        toast.success("Coach not on Rally Lab yet — invitation email sent");
      } else if (data?.status === "added") {
        toast.success("Coach added to team");
      } else {
        toast.success("Done");
      }
      setInviteEmail("");
      await load();
      // Refresh eligible list too — the newly-added coach should disappear
      try {
        const { data: elig } = await api.get(`/teams/${teamId}/eligible-coaches`);
        setEligible(elig || []);
      } catch { /* noop */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyShareLink = (id) => {
    const url = `${window.location.origin}/teams/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Public team URL copied");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">COACH TEAMS</div>
          <h1 className="h-display text-5xl tracking-tighter">Team up with other coaches.</h1>
          <div className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">Create a team to share a public dashboard players can browse — your combined offerings, credentials and coaching experience in one shareable page.</div>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="new-team-btn">
          <Plus className="w-4 h-4" /> Create team
        </button>
      </div>

      {!teams.length && (
        <div className="card-surface p-12 text-center fade-up d1">
          <UsersIcon className="w-12 h-12 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
          <div className="h-display text-2xl mb-1">No teams yet</div>
          <div className="text-sm text-[#A3A3A3]">Create one and invite other coaches by email.</div>
        </div>
      )}

      <div className="space-y-4 fade-up d1">
        {teams.length > 0 && <ListControlsBar ctl={ctl} placeholder="Search teams…" testid="teams" />}
        {ctl.list.map((t) => {
          const isOwner = t.owner_id === user?.id;
          const shareUrl = `${window.location.origin}/teams/${t.id}`;
          return (
            <div key={t.id} className="card-surface p-6" data-testid={`team-${t.id}`}>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="label-mono mb-1 text-[#CCFF00]">TEAM</div>
                  <h2 className="h-display text-2xl tracking-tight">{t.name}</h2>
                  {t.tagline && <div className="text-sm text-[#A3A3A3] mt-1">{t.tagline}</div>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-white inline-flex items-center gap-1.5" data-testid={`view-${t.id}`}>
                    View public page
                  </a>
                  <button onClick={() => copyShareLink(t.id)} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5" data-testid={`share-${t.id}`}>
                    <Share2 className="w-3.5 h-3.5" /> Copy URL
                  </button>
                  {isOwner && (
                    <button onClick={() => openInvite(t.id)} className="btn-volt px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid={`invite-${t.id}`}>
                      <Mail className="w-3.5 h-3.5" /> Invite
                    </button>
                  )}
                </div>
              </div>
              <div className="text-xs text-[#A3A3A3] mb-3 label-mono">{t.member_ids?.length || 0} member{t.member_ids?.length === 1 ? "" : "s"}{isOwner ? " · You're the owner" : ""}</div>
              {t.description && <p className="text-sm text-[#E5E5E5] leading-relaxed">{t.description}</p>}

              {activeInvite === t.id && (
                <div className="mt-4 border border-[#CCFF00] bg-[#CCFF00]/5" data-testid={`invite-panel-${t.id}`}>
                  <div className="p-4 border-b border-[#CCFF00]/40 flex items-center justify-between">
                    <div className="label-mono text-[#CCFF00]">Add coaches to team</div>
                    <button onClick={closeInvite} className="text-[#A3A3A3] hover:text-white" data-testid={`close-invite-${t.id}`}><X className="w-4 h-4" /></button>
                  </div>

                  {/* Section 1: pick from registered eligible coaches */}
                  <div className="p-4 border-b border-[#CCFF00]/40">
                    <div className="flex items-center gap-2 mb-2">
                      <UserPlus className="w-3.5 h-3.5 text-[#CCFF00]" />
                      <div className="label-mono text-[10px]">Approved coaches in your region</div>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mb-3">
                      Only registered & approved coaches sharing a region with you are listed. Coaches with lapsed
                      or missing certification / insurance appear greyed out and can&apos;t be added until they update
                      their credentials.
                    </p>

                    {eligibleLoading && (
                      <div className="text-xs text-[#A3A3A3] italic" data-testid={`eligible-loading-${t.id}`}>Loading coaches…</div>
                    )}
                    {!eligibleLoading && eligible.length === 0 && (
                      <div className="text-xs text-[#A3A3A3] italic" data-testid={`no-eligible-${t.id}`}>
                        No other approved coaches in your region yet. Use the email invite below to bring one on board.
                      </div>
                    )}
                    {!eligibleLoading && eligible.length > 0 && (
                      <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid={`eligible-list-${t.id}`}>
                        {eligible.map((c) => {
                          const certBad = c.credentials?.cert?.status === "missing" || c.credentials?.cert?.status === "lapsed";
                          const insBad = c.credentials?.insurance?.status === "missing" || c.credentials?.insurance?.status === "lapsed";
                          return (
                            <div
                              key={c.id}
                              className={`p-3 border flex items-center gap-3 ${c.eligible ? "border-[#262626] hover:border-[#CCFF00]" : "border-[#262626] opacity-40"}`}
                              data-testid={`eligible-coach-${c.id}`}
                              data-eligible={c.eligible ? "true" : "false"}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="text-sm text-white truncate">{c.name}</div>
                                  <div className="text-[10px] text-[#A3A3A3]">{c.email}</div>
                                </div>
                                {c.headline && <div className="text-[10px] text-[#A3A3A3] mt-0.5 truncate">{c.headline}</div>}
                                <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                                  <span className={`inline-flex items-center gap-1 ${certBad ? "text-[#FF4500]" : "text-[#CCFF00]"}`} data-testid={`cert-status-${c.id}`}>
                                    {certBad ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                                    Cert: {c.credentials?.cert?.status || "unknown"}
                                  </span>
                                  <span className={`inline-flex items-center gap-1 ${insBad ? "text-[#FF4500]" : "text-[#CCFF00]"}`} data-testid={`ins-status-${c.id}`}>
                                    {insBad ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                                    Insurance: {c.credentials?.insurance?.status || "unknown"}
                                  </span>
                                  {c.region_match && (
                                    <span className="inline-flex items-center gap-1 text-[#A3A3A3]"><MapPin className="w-3 h-3" /> shared region</span>
                                  )}
                                </div>
                                {!c.eligible && c.reasons?.length > 0 && (
                                  <div className="text-[10px] text-[#FF4500] mt-1" data-testid={`reasons-${c.id}`}>
                                    Cannot add: {c.reasons.map((r) => REASON_LABELS[r] || r).join(" · ")}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => addExistingCoach(t.id, c)}
                                disabled={!c.eligible || adding === c.id}
                                className="btn-volt px-3 py-1.5 text-[10px] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid={`add-coach-${c.id}`}
                              >
                                <Plus className="w-3 h-3" /> {adding === c.id ? "Adding…" : "Add"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Section 2: email a coach who isn't on Rally Lab yet */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-3.5 h-3.5 text-[#CCFF00]" />
                      <div className="label-mono text-[10px]">Invite by email (not registered yet)</div>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mb-3">
                      We&apos;ll email an application link. They can&apos;t join the team until they register,
                      get approved and share your region — but you can invite them now to kick things off.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="coach@example.com" type="email"
                        className="flex-1 min-w-[200px] bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" data-testid={`invite-email-${t.id}`} />
                      <button onClick={() => invite(t.id)} className="btn-volt px-4 py-2 text-xs" data-testid={`send-invite-${t.id}`}>Send invite</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <form onSubmit={createTeam} onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-md" data-testid="create-team-modal">
            <div className="p-6 border-b border-[#262626] flex justify-between items-center">
              <div>
                <div className="label-mono mb-1">NEW TEAM</div>
                <h2 className="h-display text-2xl">Create coach team</h2>
              </div>
              <button type="button" onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="label-mono mb-2">Team name *</div>
                <input value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} required data-testid="team-name"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
              </div>
              <div>
                <div className="label-mono mb-2">Tagline</div>
                <input value={newTeam.tagline} onChange={(e) => setNewTeam({ ...newTeam, tagline: e.target.value })} data-testid="team-tagline"
                  placeholder="e.g. North West Pickleball Performance"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
              </div>
              <div>
                <div className="label-mono mb-2">Description</div>
                <textarea value={newTeam.description} onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })} rows={3} data-testid="team-desc"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
              </div>
              <button type="submit" className="btn-volt w-full py-3 text-sm" data-testid="team-create">Create team</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
